import { twcn } from '@web/ui/core/utils';

import type { PresenceStatus } from './use_profile_menu';

const DOT: Record<PresenceStatus, string> = {
  online: 'bg-tone-green-fg',
  busy: 'bg-tone-amber-fg',
  away: 'bg-faint-foreground',
  offline: 'bg-transparent shadow-inset-faint',
};

export interface StatusChipProps {
  status: PresenceStatus;
  label: string;
  isActive: boolean;
  onSelect: (status: PresenceStatus) => void;
}

/**
 * StatusChip — toggleable presence chip used in the profile menu.
 * Mirrors `.status-chip` from the mockup: bordered pill with a
 * leading 7px dot, hover bumps the border, active swaps to the
 * subtle fill with stronger border and medium label.
 */
export function StatusChip({
  status,
  label,
  isActive,
  onSelect,
}: Readonly<StatusChipProps>): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={isActive}
      onClick={() => {
        onSelect(status);
      }}
      className={twcn(
        'inline-flex items-center gap-1.5 rounded-md border py-1 pl-2 pr-2.25 text-micro transition-colors duration-fast ease-standard',
        isActive
          ? 'border-border bg-subtle font-medium text-foreground'
          : 'border-border-soft bg-transparent text-muted-foreground hover:border-border hover:bg-muted hover:text-foreground',
      )}
    >
      <span aria-hidden className={twcn('inline-block h-1.75 w-1.75 rounded-pill', DOT[status])} />
      <span>{label}</span>
    </button>
  );
}
